<template>
  <Modal v-if="visible" :value="true" :with-footer="false" @input="closeModal">
    <span slot="header">{{ title }}</span>

    <div class="row">
      <div class="col-md-12">
        <p class="mb-3">
          Deseja realmente {{ actionLabel }} o veículo com placa <strong>{{ vehicle.plate }}</strong>?
        </p>

        <div v-if="action === 'disable'" class="alert alert-warning text-left mb-3">
          <p class="mb-2">
            <strong>Atenção:</strong> Ao inativar este veículo, ocorrerão as seguintes alterações no sistema:
          </p>
          <ul class="mb-0">
            <li>O veículo não será incluído nas próximas buscas de dados{{ modulesSuffix }}.</li>
            <li>No dashboard, os dados exibidos por padrão consideram apenas veículos ativos. Caso deseje consultar informações de veículos inativos, basta alterar o filtro <strong>Status do veículo</strong> para <strong>Inativo</strong>.</li>
            <li>Todo o histórico de dados do veículo será mantido e poderá ser consultado a qualquer momento utilizando o filtro <strong>Status do veículo: Inativo</strong>.</li>
            <li>A reativação do veículo pode ser realizada a qualquer momento, se necessário.</li>
          </ul>
        </div>

        <div v-else class="alert alert-success text-left mb-3">
          <p class="mb-2">Ao reativar este veículo, ocorrerão as seguintes alterações:</p>
          <ul class="mb-0">
            <li>O veículo será incluído nas próximas buscas de dados{{ modulesSuffix }}.</li>
            <li v-if="hasModules">Todos os dados vinculados ao veículo que estavam com status <strong>Inativo</strong> serão automaticamente atualizados para <strong>Ativo</strong> e voltarão a ser exibidos normalmente no dashboard.</li>
            <li>{{ reactivateRecordsText }}</li>
          </ul>
        </div>

        <div class="text-right mt-3">
          <button class="btn btn-outline btn-md mr-2" @click="closeModal" :disabled="loading">
            Cancelar
          </button>
          <button
            class="btn btn-md"
            :class="confirmButtonClass"
            :disabled="loading"
            @click="confirm"
          >
            {{ confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
  import Modal from '@/components/shares/specialTag/Modal'
  import GetAccount from '@/mixins/GetAccount.vue'
  import GetAccountApps from '@/mixins/GetAccountApps'
  import { getAppDisplayName } from '@/appDisplayNames'
  import { VEHICLE_MODULES, VEHICLE_STATUS } from '@/utils/vehicleStatus'

  export default {
    name: 'ModalToggleVehicleStatus',
    components: { Modal },
    mixins: [GetAccount, GetAccountApps],
    props: {
      value: { default: false, type: Boolean },
      vehicle: { type: Object },
      action: { required: true, type: String, validator: v => ['enable', 'disable'].includes(v) }
    },
    data() {
      return { loading: false }
    },
    computed: {
      visible() {
        return this.vehicle && this.value
      },
      title() {
        return this.action === 'disable' ? 'Inativar Veículo' : 'Reativar Veículo'
      },
      actionLabel() {
        return this.action === 'disable' ? 'inativar' : 'reativar'
      },
      confirmLabel() {
        return this.action === 'disable' ? 'Inativar' : 'Reativar'
      },
      confirmButtonClass() {
        return this.action === 'disable' ? 'btn-outline-red' : 'btn-primary'
      },
      isPartner() {
        return Boolean(this.getAccount && this.getAccount.accountType === 'partner')
      },
      activeVehicleModules() {
        return this.getAccountApps
          .filter(aa => aa.active && VEHICLE_MODULES.includes(aa.name))
          .map(aa => getAppDisplayName(aa.name, this.isPartner))
      },
      hasModules() {
        return this.activeVehicleModules.length > 0
      },
      modulesList() {
        const names = this.activeVehicleModules
        if (names.length === 0) return ''
        if (names.length === 1) return names[0]
        if (names.length === 2) return `${names[0]} e ${names[1]}`
        return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`
      },
      modulesSuffix() {
        return this.hasModules ? ` do ${this.modulesList}` : ''
      },
      reactivateRecordsText() {
        if (!this.hasModules)
          return 'Os registros passarão novamente a ter o status Ativo.'

        return `Os registros do ${this.modulesList} passarão novamente a ter o status Ativo.`
      }
    },
    methods: {
      closeModal() {
        if (this.loading) return
        this.$emit('input', false)
      },
      confirm() {
        if (this.loading) return
        this.loading = true

        const entityStatusId = this.action === 'disable' ? VEHICLE_STATUS.DISABLED : VEHICLE_STATUS.ENABLED

        this.axios.put(`vehicles/${this.vehicle.id}`, { entity_status_id: entityStatusId })
          .then(response => {
            if (response && response.meta && response.meta.notice)
              this.$notify.add(response.meta.notice)
            this.$emit('confirmed', this.vehicle)
            this.$emit('input', false)
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => { this.loading = false })
      }
    }
  }
</script>

<style scoped lang="scss">
  .alert {
    border-radius: 5px;
    padding: 12px 16px;
    font-size: 14px;

    p, li, strong {
      font-size: 14px;
      line-height: 1.5;
    }

    ul {
      padding-left: 20px;
      margin-bottom: 0;
    }

    li {
      margin-bottom: 4px;
    }
  }

  .alert-warning {
    background-color: var(--alert-warning-bg) !important;
    border-color: var(--alert-warning-border) !important;
    color: var(--alert-warning-text) !important;

    p, li, strong {
      color: var(--alert-warning-text) !important;
    }
  }

  .alert-success {
    background-color: #d4edda !important;
    border-color: #c3e6cb !important;
    color: #155724 !important;

    p, li, strong {
      color: #155724 !important;
    }
  }

  [data-theme="dark"] .alert-success {
    background-color: #1e3a2f !important;
    border-color: #2a5a45 !important;
    color: #75d4a0 !important;

    p, li, strong {
      color: #75d4a0 !important;
    }
  }
</style>
